import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationService } from './core.notification.service';
import { SiteConfigService } from './core.site-config.service';
import * as _ from 'lodash-es';

let logEnabled = false;

function writeDebugLog(method: string, msg: string, ...rest: any[]): void {
    // Only write these debugs on production next.gforge.com
    if (!logEnabled) return;

    const section = `PollService.${method}`;
    const now = new Date().toISOString();
    console.log(`[${now}] [${section}] ${msg}`, ...rest);
}

type PollState = 'idle' | 'running' | 'paused';

interface PollItem {
    type: string;
    section: string;
    ref_id: string | number;
    after: number | string;
}

interface PollResponseItem {
    type: string;
    section: string;
    refId: string | number;
    lastValue: number | string;
}

interface PollResponse {
    items?: PollResponseItem[];
}

// Backoff progression in seconds
const BACKOFF_STEPS = [0, 5, 10, 30, 60, 120];

@Injectable()
export class PollService implements OnDestroy {
    // ============================================================
    // Public properties (used by external code)
    // ============================================================
    running = false;
    sleepWorkaround = false;

    // ============================================================
    // Private state
    // ============================================================
    private readonly pollList = new Map<string, PollItem>();
    private state: PollState = 'idle';
    private currentRequest: Subscription | null = null;
    private nextPollTimer: Subscription | null = null;
    private backoffDelay = 0;
    private autoRun = true;
    private timeout = 5;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly httpClient: HttpClient,
        private readonly notificationService: NotificationService,
        private readonly siteConfigService: SiteConfigService,
    ) {
        logEnabled = this.siteConfigService.getConfig('appEnvironment', 'development') === 'gforge-saas';
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.cancelCurrent();
        this.cancelNextPollTimer();
        // this.stopWatchdog();
    }

    // ============================================================
    // Public API
    // ============================================================

    add(type: string, section: string, ref_id: string | number, after: number | string = 0): PollItem {
        const key = this.itemKey(type, section, ref_id);
        writeDebugLog('add', `Adding item: ${key}`);

        const wasRunning = this.running;
        if (this.running) {
            this.pause();
        }

        const pollItem: PollItem = { type, section, ref_id, after };
        this.pollList.set(key, pollItem);

        writeDebugLog('add', `Total items in poll_list: ${this.pollList.size}`);

        if (wasRunning || this.autoRun) {
            writeDebugLog('add', `Restarting polls (wasRunning=${wasRunning}, autoRun=${this.autoRun})`);
            this.run();
        }

        return pollItem;
    }

    remove(type: string, section: string, ref_id: string | number): void {
        const key = this.itemKey(type, section, ref_id);
        writeDebugLog('remove', `Removing item: ${key}`);

        if (!this.pollList.has(key)) {
            writeDebugLog('remove', 'Item not found in poll_list');
            return;
        }

        const wasRunning = this.running;
        if (this.running) {
            this.pause();
        }

        this.pollList.delete(key);
        writeDebugLog('remove', `Item removed. Remaining items: ${this.pollList.size}`);

        if (wasRunning || this.autoRun) {
            writeDebugLog('remove', `Restarting polls (wasRunning=${wasRunning}, autoRun=${this.autoRun})`);
            this.run();
        }
    }

    replace(type: string, section: string, ref_id: string | number, after: number | string): PollItem {
        this.pause();

        const newKey = this.itemKey(type, section, ref_id);
        let replacementMade = false;

        // Remove other items with same type+section
        this.pollList.forEach((item, key) => {
            if (key !== newKey && item.type === type && item.section === section) {
                this.pollList.delete(key);
                replacementMade = true;
            }
        });

        const newItem = this.add(type, section, ref_id, after);

        if (replacementMade) {
            this.run(true);
        }

        return newItem;
    }

    subscribe(dest: string, callback: (data: any) => void): void {
        this.notificationService.subscribe(dest, 'POLL_DATA', callback);
    }

    unsubscribe(dest: string): void {
        this.notificationService.unsubscribe(dest, 'POLL_DATA');
    }

    run(force = false): void {
        writeDebugLog('run', `Starting polling (force=${force}, currently running=${this.running})`);

        if (force) {
            this.cancelCurrent();
            this.cancelNextPollTimer();
        }

        if (this.state !== 'running') {
            this.setState('running');
            writeDebugLog('run', 'Set running=true, scheduling doPoll');
            this.scheduleNextPoll(100);
        } else if (force) {
            writeDebugLog('run', 'Already running, but force=true, triggering doPoll immediately');
            this.scheduleNextPoll(100);
        } else {
            writeDebugLog('run', 'Already running, nothing to do');
        }
    }

    pause(): boolean {
        writeDebugLog('pause', 'Pausing polling');
        this.setState('paused');
        this.cancelCurrent();
        this.cancelNextPollTimer();
        writeDebugLog('pause', `Paused. running=${this.running}`);
        return this.running;
    }

    reset(): void {
        this.pause();
        this.pollList.clear();
    }

    cancelCurrent(): void {
        if (this.currentRequest) {
            this.currentRequest.unsubscribe();
            this.currentRequest = null;
        }
    }

    parseJobMessage(objResponse: Record<string, any>): { msgOk: string; msgErr: string; jobId: number } {
        const ret = { msgOk: '', msgErr: '', jobId: 0 };

        Object.entries(objResponse).forEach(([key, obj]) => {
            if (/^\d+$/.test(key)) {
                if ((obj as any).status === 12) { // 12 is BackgroundJob::RESULT_SUCCESS
                    ret.msgOk += (ret.msgOk ? ', ' : '') + '#' + key;
                } else {
                    ret.msgErr += (ret.msgErr ? ', ' : '') + '#' + key;
                }
            } else if (key === 'jobId') {
                ret.jobId = obj as number;
            }
        });

        if (ret.jobId) { // objResponse only contains a jobId
            ret.msgOk = 'Your request is being processed, please wait.';
        } else { // objResponse contains list of success/failed items
            if (ret.msgOk) ret.msgOk = 'Items processed: ' + ret.msgOk;
            if (ret.msgErr) ret.msgErr = '\nItems failed: ' + ret.msgErr;
        }

        return ret;
    }

    // ============================================================
    // Private: State management
    // ============================================================

    private setState(state: PollState): void {
        this.state = state;
        this.running = state === 'running';
    }

    private shouldContinuePolling(): boolean {
        return this.state === 'running' && this.pollList.size > 0;
    }

    private itemKey(type: string, section: string, ref_id: string | number): string {
        return `${type}-${section}-${ref_id}`;
    }

    // ============================================================
    // Private: Timer management
    // ============================================================

    private cancelNextPollTimer(): void {
        if (this.nextPollTimer) {
            this.nextPollTimer.unsubscribe();
            this.nextPollTimer = null;
        }
    }

    private scheduleNextPoll(delayMs: number): void {
        this.cancelNextPollTimer();
        this.nextPollTimer = timer(delayMs)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.doPoll());
    }

    // ============================================================
    // Private: Polling logic
    // ============================================================

    private doPoll(): void {
        writeDebugLog('doPoll', `Starting (running=${this.running}, items=${this.pollList.size})`);

        if (!this.shouldContinuePolling()) {
            writeDebugLog('doPoll', 'Exiting early - poll_list empty OR not running. Pausing.');
            this.setState('paused');
            return;
        }

        if (this.currentRequest) {
            writeDebugLog('doPoll', 'Request in progress, returning early');
            return;
        }

        const requestStartTime = Date.now();
        writeDebugLog('doPoll', `Making request at ${new Date().toISOString()}`);

        this.currentRequest = this.executePollRequest()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => this.handlePollSuccess(response, requestStartTime),
                error: (error) => this.handlePollError(error, requestStartTime)
            });
    }

    private executePollRequest(): Observable<PollResponse> {
        const params = this.buildHttpParams();
        const headers = { 'X-Requested-With': 'XMLHttpRequest' };
        return this.httpClient.get<PollResponse>('/api/poll', { params, headers });
    }

    private handlePollSuccess(response: PollResponse, requestStartTime: number): void {
        writeDebugLog('doPoll', 'SUCCESS response received', response);
        this.currentRequest = null;

        if (this.checkAndHandleSleepState(requestStartTime)) {
            return;
        }

        this.backoffDelay = 0;
        this.dispatchResponse(response);
        this.scheduleNextPollIfRunning(100);
    }

    private handlePollError(error: any, requestStartTime: number): void {
        console.error('[PollService.doPoll] ERROR response:', error);
        this.currentRequest = null;

        // Don't keep running if you're not allowed/logged in
        if ([401, 403].includes(error.status)) {
            console.error(`[PollService.doPoll] Auth error (${error.status}) - STOPPING POLLS`);
            this.notificationService.notifyAll('POLL_DATA', {
                response: false,
                error: { status: error.status },
                params: this.buildRequestParams()
            });
            this.setState('idle');
            return;
        }

        if (this.checkAndHandleSleepState(requestStartTime)) {
            return;
        }

        // Backoff and retry
        const previousDelay = this.backoffDelay;
        this.backoffDelay = this.calculateNextBackoff(this.backoffDelay);
        writeDebugLog('doPoll', `Backoff: ${previousDelay}s -> ${this.backoffDelay}s. Scheduling retry.`);
        this.scheduleNextPollIfRunning(this.backoffDelay * 1000);
    }

    private checkAndHandleSleepState(requestStartTime: number): boolean {
        if (this.sleepWorkaround && this.isFromSleepState(requestStartTime)) {
            writeDebugLog('[PollService.doPoll]', 'Sleep state detected - STOPPING POLLS');
            this.setState('paused');
            return true;
        }
        return false;
    }

    private scheduleNextPollIfRunning(delayMs: number): void {
        if (this.shouldContinuePolling()) {
            writeDebugLog('doPoll', `Scheduling next poll in ${delayMs}ms`);
            this.scheduleNextPoll(delayMs);
        } else {
            writeDebugLog('doPoll', 'Not running, not scheduling next poll');
        }
    }

    // ============================================================
    // Private: Response handling
    // ============================================================

    private dispatchResponse(response: PollResponse): void {
        const items = this.normalizeItems(response?.items);

        writeDebugLog('dispatch', `Received ${items.length} items`);
        items.forEach(item => this.updateItem(item));

        this.notificationService.notifyAll('POLL_DATA', { response: { items } });
    }

    /**
     * The API answers with a flat list [ {}, {}, ... ] when there is new
     * data, and with the request's own map of lists { type: [ {}, ... ] }
     * when the long poll timed out. There can be several results
     * of the same type (one chat room per open thread, say), so both shapes
     * are flattened to one list.
     */
    private normalizeItems(raw: any): PollResponseItem[] {
        let items: any[];
        if (Array.isArray(raw)) {
            items = raw;
        } else if (_.isPlainObject(raw)) {
            items = Object.values(raw).flatMap((value: any) => _.castArray(value));
        } else {
            items = [];
        }
        return items.filter(item => _.isObject(item));
    }

    private updateItem(item: PollResponseItem): void {
        const key = this.itemKey(item.type, item.section, item.refId);
        const existing = this.pollList.get(key);

        if (!existing) return;

        // The second part of the if is overly-complicated
        // because tickler sends 'x' for the initial poll,
        // and it doesn't compare well with the 0 that is
        // returned.
        const after = (existing.after ?? 0) as number;
        if (typeof item.lastValue === 'number' && !(after > item.lastValue)) {
            existing.after = item.lastValue;
        } else if (after != item.lastValue) {
            existing.after = item.lastValue;
        }
    }

    // ============================================================
    // Private: Request building
    // ============================================================

    private buildRequestParams(): Record<string, any> {
        const result: Record<string, any> = {
            interval: this.timeout,
            rel: ''
        };
        const rels: string[] = [];

        this.pollList.forEach(item => {
            const relKey = `${item.type}[]`;
            if (!result[relKey]) {
                rels.push(item.type);
                result[relKey] = [];
            }
            result[relKey].push(`${item.section}-${item.ref_id}-${item.after}`);
        });

        result.rel = rels.join(',');
        return result;
    }

    private buildHttpParams(): HttpParams {
        let params = new HttpParams();

        Object.entries(this.buildRequestParams()).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(v => {
                    params = params.append(key, String(v));
                });
            } else {
                params = params.set(key, String(value));
            }
        });

        return params;
    }

    // ============================================================
    // Private: Utilities
    // ============================================================

    private calculateNextBackoff(current: number): number {
        const currentIndex = BACKOFF_STEPS.indexOf(current);
        if (currentIndex >= 0 && currentIndex < BACKOFF_STEPS.length - 1) {
            return BACKOFF_STEPS[currentIndex + 1];
        }
        // Beyond the defined steps, double up to max
        return Math.min((current || 5) * 2, BACKOFF_STEPS[BACKOFF_STEPS.length - 1]);
    }

    private isFromSleepState(requestStartTime: number): boolean {
        const elapsed = (Date.now() - requestStartTime) / 1000;
        return elapsed > (this.timeout + 2) * 2;
    }

    // ============================================================
    // Watchdog
    // ============================================================

    // RMT 2025-11-21 [#63998] Added in case it's needed - not currently used.
    //                If the cancelRequest cleanup issue fixes race conditions,
    //                then we can remove this code.  Otherwise, we can try running it.
    private watchdogInterval: any = null;

    private startWatchdog(): void {
        if (this.watchdogInterval) return;

        this.watchdogInterval = setInterval(() => {
            const shouldBeRunning = this.pollList.size > 0 && this.autoRun;
            const isStuck = !this.running && !this.currentRequest;

            if (shouldBeRunning && isStuck) {
                writeDebugLog('watchdog', 'Detected stuck state, restarting polling');
                this.run(true);
            }
        }, 30000); // Check every 30 seconds
    }

    private stopWatchdog(): void {
        if (this.watchdogInterval) {
            clearInterval(this.watchdogInterval);
            this.watchdogInterval = null;
        }
    }
}